import { useState } from 'react';
import useSyncedStorage from './useSyncedStorage';
import { searchChristianSongs } from '../services/songApi';
import analyticsService from '../services/analytics';
import { toast } from '../lib/toast';

// The saved song library (synced per login) plus the "add a song" form and
// the online song search. Songs are { title, lyrics } (online results may
// carry extra fields such as `artist`).
export default function useSongLibrary({ addDebugLog, logActivity }) {
  const [songLibrary, setSongLibrary] = useSyncedStorage('songLibrary', (stored) => stored || []);
  const [songSearch, setSongSearch] = useState('');
  const [showAddSongForm, setShowAddSongForm] = useState(false);
  const [newSongTitle, setNewSongTitle] = useState('');
  const [newSongLyrics, setNewSongLyrics] = useState('');
  const [newSongCcli, setNewSongCcli] = useState('');
  const [onlineSearchQuery, setOnlineSearchQuery] = useState('');
  const [onlineSearchResults, setOnlineSearchResults] = useState([]);
  const [isSearchingOnline, setIsSearchingOnline] = useState(false);

  const addToSongLibrary = () => {
    if (!newSongTitle.trim()) {
      toast.error('Please enter a song title');
      return;
    }
    if (!newSongLyrics.trim()) {
      toast.error('Please enter the song lyrics');
      return;
    }

    const newSong = {
      title: newSongTitle.trim(),
      lyrics: newSongLyrics.trim()
    };
    if (newSongCcli.trim()) newSong.ccliNumber = newSongCcli.trim();

    setSongLibrary(prev => [...prev, newSong]);
    setNewSongTitle('');
    setNewSongLyrics('');
    setNewSongCcli('');
    setShowAddSongForm(false);
    addDebugLog(`🎵 Added song: ${newSong.title}`);
    toast.success(`Added "${newSong.title}" to your songs`);
  };

  const removeFromSongLibrary = (index) => {
    setSongLibrary(prev => prev.filter((_, i) => i !== index));
    addDebugLog(`🎵 Removed from song library`);
  };

  const clearSongLibrary = () => {
    if (songLibrary.length > 0 && window.confirm(`Clear all ${songLibrary.length} songs from library?`)) {
      setSongLibrary([]);
      addDebugLog(`🎵 Cleared song library`);
    }
  };

  // Used for songs found by online search and by the AI finder.
  const addFoundSongToLibrary = (song) => {
    const exists = songLibrary.some(s => s.title.toLowerCase() === song.title.toLowerCase());
    if (exists) {
      toast.info('This song is already in your library');
      return;
    }

    setSongLibrary(prev => [...prev, song]);
    addDebugLog(`✅ Added "${song.title}" to library`);
    toast.success(`Added "${song.title}" to your songs`);
  };

  const searchSongsOnline = async () => {
    if (!onlineSearchQuery.trim()) {
      toast.error('Please enter a song name to search');
      return;
    }

    setIsSearchingOnline(true);
    addDebugLog(`🔍 Searching online for: "${onlineSearchQuery}"`);
    analyticsService.trackEvent('song_search', {
      query: onlineSearchQuery
    });
    logActivity('song_search', {
      query: onlineSearchQuery,
      searchType: 'online'
    });

    try {
      const results = await searchChristianSongs(onlineSearchQuery);
      setOnlineSearchResults(results);

      if (results.length > 0) {
        addDebugLog(`✅ Found ${results.length} song(s) online`);
      } else {
        addDebugLog(`⚠️ No songs found for "${onlineSearchQuery}"`);
      }
    } catch (error) {
      addDebugLog(`❌ Online search failed: ${error.message}`);
    } finally {
      setIsSearchingOnline(false);
    }
  };

  const clearOnlineSearchResults = () => {
    setOnlineSearchResults([]);
    setOnlineSearchQuery('');
    addDebugLog(`🧹 Cleared online search results`);
  };

  return {
    songLibrary,
    songSearch,
    setSongSearch,
    showAddSongForm,
    setShowAddSongForm,
    newSongTitle,
    setNewSongTitle,
    newSongLyrics,
    setNewSongLyrics,
    newSongCcli,
    setNewSongCcli,
    onlineSearchQuery,
    setOnlineSearchQuery,
    onlineSearchResults,
    isSearchingOnline,
    addToSongLibrary,
    removeFromSongLibrary,
    clearSongLibrary,
    addFoundSongToLibrary,
    searchSongsOnline,
    clearOnlineSearchResults,
  };
}
